import { Box, Button, Collapse, Heading, Stack } from '@chakra-ui/react'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { useState } from 'react'

import type { WorldLevel } from '@/queries/constants'

import Icon from '@/components/UI/Icon/Icon'

import type { WorldBase } from '../types'
import { useAllTodos, useSeriesPageTodos } from './hooks/useTodos'
import TodoSingle from './TodoSingle'
import type { TodoItem } from './types'

const DEFAULT_MAX_TO_SHOW = 3
const DEFAULT_MAX_TO_SHOW_INCLUDING_COLLAPSE = Number.POSITIVE_INFINITY

// If tutor, exclude todos for all levels tutor or above.
export type WorldTodoExcludeTargetLevel = Extract<WorldLevel, 'tutor' | 'member'>

export type WorldDetailsForTodoList = Pick<WorldBase, 'iconUrl' | 'title' | 'primaryColor' | 'slug'>

interface TodoListProps {
  heading?: string
  headingSize?: string
  excludeTargetLevel?: WorldTodoExcludeTargetLevel
  maxToShow?: number
  maxToShowAfterCollapse?: number
  includeWorldInfo?: boolean
  includeDisabled?: boolean
  excludeGeneralTodos?: boolean
}

interface TodoListInternalProps extends TodoListProps {
  todos: (TodoItem & { world: WorldDetailsForTodoList | null })[]
}

function TodoListInternal({
  todos,
  heading = 'Tasks',
  headingSize = 'lg',
  excludeTargetLevel,
  maxToShow = DEFAULT_MAX_TO_SHOW,
  maxToShowAfterCollapse = DEFAULT_MAX_TO_SHOW_INCLUDING_COLLAPSE,
  includeWorldInfo = false,
  includeDisabled = false,
}: TodoListInternalProps): React.ReactElement | null {
  const incompleteTodos = todos
    .filter(todo => {
      if (excludeTargetLevel === 'tutor') {
        return todo.targetLevel == null || todo.targetLevel === 'member'
      }
      if (excludeTargetLevel === 'member') {
        return todo.targetLevel !== 'member'
      }
      return true
    })
    .filter(todo => !todo.completed && (includeDisabled ? true : todo.disabled == null))
    .slice(0, maxToShowAfterCollapse)

  const [showAll, setShowAll] = useState(false)

  if (incompleteTodos.length === 0) {
    return null
  }

  return (
    <Box>
      <Heading size={headingSize} mb={{ base: '8px', md: '16px' }}>
        {heading}
      </Heading>
      <Stack direction="column" spacing="8px" width="100%">
        {incompleteTodos.slice(0, maxToShow).map(todo => (
          <TodoSingle
            key={`${todo.title}-${todo.worldSlug}`}
            includeWorldInfo={includeWorldInfo}
            {...todo}
          />
        ))}
      </Stack>
      <Collapse in={showAll}>
        <Stack direction="column" spacing="8px" width="100%" mt="8px">
          {incompleteTodos.slice(maxToShow).map(todo => (
            <TodoSingle
              key={`${todo.title}-${todo.worldSlug}`}
              includeWorldInfo={includeWorldInfo}
              {...todo}
            />
          ))}
        </Stack>
      </Collapse>
      {incompleteTodos.length > maxToShow ? (
        <Button
          mt={{ base: '4px', md: '8px' }}
          variant="ghost"
          onClick={() => setShowAll(!showAll)}
          rightIcon={<Icon size="md" faIcon={showAll ? faChevronUp : faChevronDown} />}
        >
          {showAll ? 'Show fewer' : 'Show more'}
        </Button>
      ) : null}
    </Box>
  )
}

export function SeriesTodoList({
  world,
  ...props
}: {
  world: WorldDetailsForTodoList
} & TodoListProps): React.ReactElement | null {
  const todos = useSeriesPageTodos({ world })
  return <TodoListInternal todos={todos} {...props} />
}

export function TodoList({
  worldSlugs,
  ...props
}: { worldSlugs: string[] } & TodoListProps): React.ReactElement | null {
  const todos = useAllTodos({
    worldSlugs,
    excludeGeneralTodos: props.excludeGeneralTodos,
  })
  return <TodoListInternal todos={todos} {...props} />
}
